/**
 * 前后台生命周期接线（任务 13b，requests 场景 I/J）：
 * - 隐藏→可见沿（含 bfcache 恢复的 pageshow persisted）、**网络恢复（online 事件，
 *   21ah）** → 'chat/wake' 探活（后者让「网回来了还卡在 30s 退避里」当场结束）。
 *   13a 口径：WS 存活零请求；断开/假死由 Worker 当场重连补拉，结果走既有
 *   replace/invalidate 推送——这里不看返回值。未登录不调（无 session 恒 false）。
 * - 可见→隐藏沿 + pagehide（覆盖 standalone 进程回收/关标签前的最后时机）→
 *   把打开中会话的滚动 anchor 回传 Worker 存恢复点（restoreState.flushRestoreAnchor）。
 *
 * 纯逻辑在 createWakeTrigger（边沿判定 + 抖动合并，可单测）；DOM 接线在
 * connectWake（main.tsx 启动时调一次）。
 */

import { flushRestoreAnchor } from './restoreState';
import type { GlobalStore } from './global';
import type { AppWorkerHost } from './authState';

/** visibilitychange 与 pageshow persisted 常背靠背触发，窗口内只探活一次 */
export const WAKE_COOLDOWN_MS = 1_000;

export interface WakeTrigger {
  /** document 可见性变化（true=可见）。同值重复喂幂等 */
  visibility(visible: boolean): void;
  /** window pageshow。persisted=true（bfcache 复活，WS 必死）才算回前台 */
  pageShow(persisted: boolean): void;
  /** window pagehide：无条件存一次 anchor（与隐藏沿重复写幂等，宁多勿漏） */
  pageHide(): void;
  /**
   * window online（21ah）：网络恢复即探活，不等 WS 自己的退避走完。
   * 退避最长 30s（ws/client.scheduleRetry），而断网 10 分钟回来时退避一定已经
   * 顶到 30s——此时用户看到的是「网恢复了还是发不出去」。probe 在退避档会跳过
   * 剩余等待立即开 socket，所以这里接上就是最快的一条路。与可见性沿共用
   * 冷却窗口（两者常背靠背触发：拔网线切 Wi-Fi 往往伴随切前台）。
   */
  online(): void;
}

export function createWakeTrigger(deps: {
  initialVisible: boolean;
  now(): number;
  onWake(): void;
  onHide(): void;
}): WakeTrigger {
  let visible = deps.initialVisible;
  let lastWakeAt = Number.NEGATIVE_INFINITY;

  function wake(): void {
    const t = deps.now();
    if (t - lastWakeAt < WAKE_COOLDOWN_MS) return;
    lastWakeAt = t;
    deps.onWake();
  }

  return {
    visibility(v) {
      if (v === visible) return;
      visible = v;
      if (v) wake();
      else deps.onHide();
    },
    pageShow(persisted) {
      if (!persisted) return; // 首次加载的 pageshow 不是「回前台」
      visible = true;
      wake();
    },
    pageHide() {
      deps.onHide();
    },
    online() {
      // 后台恢复网络也探活：WS 在后台照样该连上（推送/未读要跟着走），
      // 而且下次切回前台时连接已经是热的
      wake();
    },
  };
}

export function connectWake(store: GlobalStore, host: AppWorkerHost): () => void {
  const trigger = createWakeTrigger({
    initialVisible: document.visibilityState !== 'hidden',
    now: () => Date.now(),
    onWake: () => {
      if (store.get().auth.status !== 'logged-in') return;
      void host.call('chat/wake', null).catch(() => undefined);
    },
    onHide: () => flushRestoreAnchor(store, host),
  });
  const onVisibility = (): void => trigger.visibility(document.visibilityState !== 'hidden');
  const onPageShow = (e: PageTransitionEvent): void => trigger.pageShow(e.persisted);
  const onPageHide = (): void => trigger.pageHide();
  const onOnline = (): void => trigger.online();
  document.addEventListener('visibilitychange', onVisibility);
  window.addEventListener('pageshow', onPageShow);
  window.addEventListener('pagehide', onPageHide);
  window.addEventListener('online', onOnline);
  return () => {
    window.removeEventListener('online', onOnline);
    document.removeEventListener('visibilitychange', onVisibility);
    window.removeEventListener('pageshow', onPageShow);
    window.removeEventListener('pagehide', onPageHide);
  };
}
